import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./family-match-report-style.css";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import SiteFooter from "../../../custom/site-footer/site-footer";
import useIsMobile from "../../../../hooks/useIsMobile";
import { shareOrCopy } from "../../../../utils/share";

//分享 / 官方 LINE（footer 用）
const SHARE_URL = "https://yuanchen.leinotek.com/";
const LINE_URL = "https://lin.ee/v05Pe75";
const handleShare = (e) => {
  e.preventDefault();
  shareOrCopy({
    title: "元辰雲閣",
    text: "元辰雲閣｜親子合盤・讀懂孩子的天性",
    url: SHARE_URL,
  }).then((r) => {
    if (r === "copied") window.alert("已複製連結，貼上即可分享");
  });
};

//天賦五維（固定順序，雷達圖 + 星級列表共用）
const TALENT_KEYS = [
  { key: "logic", label: "邏輯思維" },
  { key: "endurance", label: "執行耐力" },
  { key: "social", label: "人際協調" },
  { key: "art", label: "感官藝術" },
  { key: "intuition", label: "直覺靈感" },
];

//—— 暫時的假資料（之後接 Gemini 後台再換）——
const mockData = {
  productID: "1234567890",
  orderID: "DI_MOCK_FAMILY",
  slogan: "親子地圖：雙向深度實戰版",
  parent: {
    name: "陳宜鎧",
    gender: "女",
    birth: "1995/12/13",
  },
  child: {
    name: "宋里霧",
    gender: "男",
    birth: "2023/02/08",
  },
  //孩子的性格本質
  childNature: {
    title: "冷靜外表下的熾熱靈魂",
    content:
      "<p>彤彤在命理上屬於「寒冬之木」（甲木生於丑月），這賦予了她超越同齡人的穩重與深沉。這孩子宛如一棵聳立於雪地中的松柏，<b>外表看似平靜冷淡，內心實則極度渴望陽光般的認可</b>。她對「責任感」與「是非對錯」有著近乎偏執的堅持，習慣在心中劃下一道高標準的紅線，默默要求自己必須達標。</p><p>家長常覺得她「倔強」或「難溝通」，但那其實是她獨有的「防禦性沉默」。<b>由於她自尊心極強且深怕被否定，一旦察覺語氣嚴肅或帶有指責，她的自我保護機制便會瞬間築起高牆</b>。此時的她，表面雖無反應，內心卻在反覆檢討，甚至因擔心多說多錯而被扣分，索性關閉溝通大門。她並非刻意抗議，只是在那道冷冽的防線後，小心翼翼地守護著自己不被打碎。</p>",
  },
  //天賦雷達圖
  talentRadar: {
    //雷達圖五維分數（1-5）
    scores: {
      logic: 5,
      endurance: 5,
      social: 3,
      art: 2,
      intuition: 2,
    },
    //各項說明（與 scores 同 key）
    descriptions: {
      logic: "擅長因果判斷與結構分析",
      endurance: "具備高度專注力與自我要求",
      social: "嚴守規矩但處事較為被動",
      art: "重視實務與簡約審美",
      intuition: "思維高度結構化，較少天馬行空",
    },
    //【個性分析與領域建議】
    fieldAnalysis:
      "<p>根據彤彤「<b>內斂、邏輯強、守規矩</b>」的天性，她天生適合在專業壁壘高、層級明確的領域深耕。她並非那種依賴人脈應酬或花言巧語生存的類型，而是典型的「實力派」。對她而言，最理想的工作狀態是「憑本事說話」，<b>需要一份具有技術門檻且資歷越久越吃香的職涯</b>。在混亂無序的環境中，她的能量會被消磨內耗；但在秩序井然的體系裡，她則能展現出驚人的穩定性與深度。</p>",
    //領域建議清單
    fields: [
      {
        name: "法律司法",
        desc: "適合法官或律師，運用嚴密的條文邏輯處理高壓事務。",
      },
      {
        name: "醫學科研",
        desc: "適合外科手術或藥學研究，發揮高度精確度與穩定情緒。",
      },
      {
        name: "精密工程",
        desc: "適合結構設計、程式開發或數據分析，展現強大的結構化思維。",
      },
      {
        name: "高階公職",
        desc: "在穩定的體制內依循規則，隨著資歷累積建立專業。",
      },
    ],
  },
  //你們的相處情況
  relationship: {
    content:
      "<p>王先生，您與彤彤的<b>個性極其相似</b>，這在命理上稱為「比劫同類」。您們兩位都非常有主見、自尊心強且不輕易低頭。關係和諧時，您們極具默契，因為彼此都認同「要把事情做好」的底線，那種正直且不佔人便宜的價值觀高度一致，往往一個眼神就能傳達信任。</p><p>然而，當意見分歧時，問題在於您們都太習慣以「防禦」面對彼此。<b>您習慣以「教導」下達指令來維持父親的尊嚴，但彤彤感受到的卻是「主權被侵犯」的壓迫感</b>，進而以沉默或僵硬的態度來維護自主權。這本質上是一場「硬脾氣對抗硬脾氣」的角力。最令人疲憊的是，衝突後明明雙方都在意彼此，卻因沒人願意先示弱而陷入漫長的冷戰。這種「渴望靠近卻互相推開」的循環，正是您們關係中急需解開的死結。</p>",
  },
  //角色鏡像分析
  roleMirror: {
    childView:
      "<p>從彤彤的「父母宮」來看，帶有強烈的官殺氣息。這代表在她眼裡，您不單是位父親，更像是一位隨時在評分、威嚴十足的「教官」。當您皺眉或語氣低沉時，她接收到的訊號並非關心，而是「我又出錯、要被糾正了」的警報。</p>",
    parentLesson:
      "<p>您必須理解，對抗一個同樣強勢的孩子，贏了也是輸。她是您的鏡子，反映出您性格中剛硬的那一面。這孩子是來教導您學會「柔軟」與「示弱」的。當您愈能放下教官架子，坦誠自己的疲憊或無助，她才會真正為您卸下心防。</p>",
  },
  //衝突預警與對策
  conflicts: [
    {
      title: "可能衝突一：當您的「提醒」被解讀為「管太多」",
      reason:
        "<p>您們兩位在八字上皆屬「甲木」，具備強烈的主導慾與領地意識。對您而言，提醒是父親的責任與關懷；但對彤彤來說，這卻像在宣告「妳沒能力管好自己」。當指令進入她的地盤，她會感到自主權受損（比劫互爭），為了守護自尊而反射性地回嗆「我知道啦」。這其實是她在保護那顆渴望獨立的心，不希望總是被當成被動受教的小孩。</p>",
      advice:
        "<p>將指令轉換為「徵詢」。試著改口：「這件事妳預計幾點處理？有需要我幫忙的地方嗎？」將決定權還給她，她為了證明自己「真的懂」，表現反而會更好。</p>",
    },
    {
      title: "可能衝突二：當您氣「態度」，她爭「邏輯」",
      reason:
        "<p>這是溝通頻率完全錯位的碰撞。您重視尊卑秩序（官），認為晚輩說話應有分寸；但彤彤的思考核心是是非對錯（印）。爭執時若您指責她「態度差」，她會認為您是因為道理講不贏，才動用長輩身份「以大壓小」。這會讓她更不服氣，進而封閉溝通。她並非故意無禮，只是認為在「真理」面前，情緒和語氣並不重要。</p>",
      advice:
        "<p>先「冷靜降溫」，再討論事實。當下不糾結態度，等情緒過後再針對「如何解決問題」進行客觀討論。只要道理講通了，她自然會願意配合。</p>",
    },
    {
      title: "可能衝突三：當您的「高效率」撞上她的「慢精準」",
      reason:
        "<p>您的性格行動力強、求快求變；但彤彤追求的是事情的完整度與收尾的「精準感」。當您催促「快一點」時，對她而言並非加速，而是活生生地切斷了她正在運作的思緒。這種被打亂的焦慮會觸發她的保護機制（印星受損），導致大腦斷線甚至暴怒。她發脾氣擺爛說「不去了」，其實是因節奏被打亂、無法完成任務時的一種情緒防衛。</p>",
      advice:
        "<p>執行「提早預告」。出發前 15 分鐘先行告知，給予她大腦收尾的緩衝時間，能大幅減少因突發變動產生的摩擦。</p>",
    },
  ],
  //給父母的功課：本週轉變練習
  weeklyPractice: [
    {
      title: "情感降溫練習",
      content:
        "<p>當衝突發生時請主動喊停，上前給孩子一個擁抱。這段時間請保持完全沉默，不解釋、不道歉。身體的接觸能直接傳遞「我依然愛妳」的訊號，用溫度化解僵化的氣氛。</p>",
    },
    {
      title: "認可練習",
      content:
        "<p>每天找一件「不需要做得完美，但她有在努力」的事實。例如：「我看妳今天就算很累，還是先收好了餐具，我覺得這點很負責。」讓她明白您的愛並非建立在完美的表現之上。</p>",
    },
    {
      title: "權力讓渡練習",
      content:
        "<p>選定一件小事（如：去哪吃飯、看哪部片），正式告訴她：「這件事妳決定就好，我們都配合妳。」透過完全的服從傳遞「信任」，這比任何說教都能讓她感受到被尊重。</p>",
    },
  ],
  //溫馨結語
  conclusion: {
    content:
      "<p><b>爸爸，請記住</b>：「你的信任，是她最堅固的盔甲；你的焦慮，則是她平庸的開始。」對於這種自尊心極強的孩子，她需要的不是一個監督者，而是一位「戰略顧問」。</p><p>當您學會放下心中的尺，不再用您的標準去框限她，她反而會展翅飛往令您驚訝的高處。教養是一場「得體的退出」，您的溫柔與信任，就是她面對世界最大的勇氣。</p>",
  },
};

const FamilyMatchReportComponent = ({
  data: propData,
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  const isMobile = useIsMobile();

  const reelRef = useRef(null);
  const outerPaperRef = useRef(null);
  const topHolderRef = useRef(null);
  const bottomHolderRef = useRef(null);
  const mobileReelRef = useRef(null);
  const mobileOuterPaperRef = useRef(null);
  const mobileTopHolderRef = useRef(null);
  const mobileBottomHolderRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  //資料來源：propData（後台「查看產品」直接帶入）優先 → navigate state → mock
  const data = propData ?? location.state?.data ?? mockData;

  //網頁版卷軸開場動畫（與 love-match 同一套：bottom-holder 由 top 位置往下展開 + paper clip-path）
  useEffect(() => {
    const reel = reelRef.current;
    const paper = outerPaperRef.current;
    const topHolder = topHolderRef.current;
    const holder = bottomHolderRef.current;
    if (!reel || !paper || !topHolder || !holder) return;

    let rafId;

    const startAnimation = () => {
      holder.style.transform = "";
      const initialY =
        topHolder.getBoundingClientRect().top -
        holder.getBoundingClientRect().top;
      holder.style.transform = `translateY(${initialY}px)`;
      holder.style.opacity = "1";

      const DURATION = 3500;
      const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
      let startTime = null;

      const animate = (ts) => {
        if (!startTime) startTime = ts;
        const t = Math.min((ts - startTime) / DURATION, 1);
        const p = easeInOut(t);
        holder.style.transform = `translateY(${initialY * (1 - p)}px)`;
        paper.style.clipPath = `inset(0 0 ${(1 - p) * 100}% 0)`;
        if (t < 1) rafId = requestAnimationFrame(animate);
      };

      rafId = requestAnimationFrame(animate);
    };

    const checkAndStart = () => {
      if (topHolder.complete && holder.complete) startAnimation();
    };

    topHolder.addEventListener("load", checkAndStart);
    holder.addEventListener("load", checkAndStart);
    checkAndStart();

    return () => {
      topHolder.removeEventListener("load", checkAndStart);
      holder.removeEventListener("load", checkAndStart);
      cancelAnimationFrame(rafId);
    };
  }, []);

  //手機版卷軸開場動畫
  useEffect(() => {
    const reel = mobileReelRef.current;
    const paper = mobileOuterPaperRef.current;
    const topHolder = mobileTopHolderRef.current;
    const holder = mobileBottomHolderRef.current;
    if (!reel || !paper || !topHolder || !holder) return;

    let rafId;

    const startAnimation = () => {
      holder.style.transform = "";
      const initialY =
        topHolder.getBoundingClientRect().top -
        holder.getBoundingClientRect().top;
      holder.style.transform = `translateY(${initialY}px)`;
      holder.style.opacity = "1";

      const DURATION = 3500;
      const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
      let startTime = null;

      const animate = (ts) => {
        if (!startTime) startTime = ts;
        const t = Math.min((ts - startTime) / DURATION, 1);
        const p = easeInOut(t);
        holder.style.transform = `translateY(${initialY * (1 - p)}px)`;
        paper.style.clipPath = `inset(0 0 ${(1 - p) * 100}% 0)`;
        if (t < 1) rafId = requestAnimationFrame(animate);
      };

      rafId = requestAnimationFrame(animate);
    };

    const checkAndStart = () => {
      if (topHolder.complete && holder.complete) startAnimation();
    };

    topHolder.addEventListener("load", checkAndStart);
    holder.addEventListener("load", checkAndStart);
    checkAndStart();

    return () => {
      topHolder.removeEventListener("load", checkAndStart);
      holder.removeEventListener("load", checkAndStart);
      cancelAnimationFrame(rafId);
    };
  }, []);

  //雷達圖資料（依固定順序）
  const radarChartData = TALENT_KEYS.map(({ key, label }) => ({
    label,
    score: data.talentRadar?.scores?.[key] ?? 0,
    full: 5,
  }));

  //星級列表（依固定順序）— 對應圖中【邏輯思維 / 執行耐力 / 人際協調 / 感官藝術 / 直覺靈感】五項
  const renderTalentList = () =>
    TALENT_KEYS.map(({ key, label }) => {
      const score = data.talentRadar?.scores?.[key] ?? 0;
      const desc = data.talentRadar?.descriptions?.[key] ?? "";
      return (
        <div className="talent-row" key={key}>
          <div className="talent-head">
            <span className="talent-label">{label}：</span>
            <div className="talent-stars">
              {Array.from({ length: score }).map((_, i) => (
                <img key={i} src="/images/ic-yellow-star.png" alt="" />
              ))}
            </div>
          </div>
          <span className="talent-desc">{desc}</span>
        </div>
      );
    });

  //「報告主體」內層共用 markup（web / mobile 兩邊共用）
  const renderInnerContent = () => (
    <>
      <div className="role-label">家長 / 孩子</div>
      <div className="between-info">
        <div className="user">
          <span>{data.parent?.name}</span>
          <span>{`${data.parent?.gender}性`}</span>
          <span>{data.parent?.birth}</span>
        </div>
        <img
          className="family-holder"
          src="/images/ic-family-report.png"
          alt=""
        />
        <div className="user">
          <span>{data.child?.name}</span>
          <span>{`${data.child?.gender}性`}</span>
          <span>{data.child?.birth}</span>
        </div>
      </div>

      {/* 卷頭裝飾標題 */}
      <div className="reel-title">
        <img src="/images/bg-love-top-title.png" alt="" />
        <span className="label">專屬你的親子合盤</span>
        <span className="title">{data.slogan}</span>
        <img src="/images/bg-love-bottom-title.png" alt="" />
      </div>

      {/* 1. 孩子的性格本質 */}
      <span className="item-label">
        孩子的性格本質：
        <br />
        {data.childNature?.title}
      </span>
      <div
        className="item-block html"
        dangerouslySetInnerHTML={{ __html: data.childNature?.content ?? "" }}
      />

      {/* 2. 天賦雷達圖 */}
      <span className="item-label">
        孩子的天賦雷達圖
        <br />
        解碼潛能與職涯導向
      </span>
      <div className="radar-wrap">
        <RadarChart
          width={isMobile ? 300 : 420}
          height={isMobile ? 300 : 420}
          data={radarChartData}
          outerRadius="65%"
          style={{ pointerEvents: "none" }}
        >
          <PolarGrid
            gridType="polygon"
            stroke="#c6c6c6"
            strokeDasharray="8 4"
          />
          <PolarAngleAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{
              fill: "#615557",
              fontSize: isMobile ? 12 : 15,
              fontWeight: 500,
            }}
          />
          <PolarRadiusAxis
            domain={[0, 5]}
            ticks={[1, 2, 3, 4, 5]}
            tick={false}
            axisLine={false}
          />
          <Radar
            dataKey="full"
            stroke="#707070"
            strokeWidth={3}
            fill="none"
            dot={false}
            isAnimationActive={false}
          />
          <Radar
            name="天賦"
            dataKey="score"
            fill="#f5c08a"
            fillOpacity={0.65}
            stroke="#d99458"
            strokeWidth={1.5}
          />
        </RadarChart>
      </div>

      {/* 天賦五維星級列表 */}
      <div className="talent-list">{renderTalentList()}</div>

      {/* 個性分析與領域建議 */}
      <div className="item-block field-block">
        <span className="block-head">【個性分析與領域建議】</span>
        <div
          className="html"
          dangerouslySetInnerHTML={{
            __html: data.talentRadar?.fieldAnalysis ?? "",
          }}
        />
        <ul className="field-list">
          {(data.talentRadar?.fields ?? []).map((f, i) => (
            <li key={i}>
              <b>{f.name}</b>：{f.desc}
            </li>
          ))}
        </ul>
      </div>

      {/* 3. 你們的相處情況 */}
      <span className="item-label">你們的相處情況</span>
      <div
        className="item-block html"
        dangerouslySetInnerHTML={{ __html: data.relationship?.content ?? "" }}
      />

      {/* 4. 角色鏡像分析 */}
      <span className="item-label">角色鏡像分析</span>
      <div className="item-block">
        <span className="block-head">● 孩子眼中的您：</span>
        <div
          className="html"
          dangerouslySetInnerHTML={{ __html: data.roleMirror?.childView ?? "" }}
        />
        <span className="block-head spaced">● 父母的修煉功課：</span>
        <div
          className="html"
          dangerouslySetInnerHTML={{
            __html: data.roleMirror?.parentLesson ?? "",
          }}
        />
      </div>

      {/* 5. 衝突預警與對策 */}
      <span className="item-label">衝突預警與對策</span>
      <div className="item-block">
        {(data.conflicts ?? []).map((c, i) => (
          <div className="conflict-item" key={i}>
            <span className="block-head">● {c.title}</span>
            <div className="reason-line">
              <span className="reason-tag">原因：</span>
              <div
                className="html inline"
                dangerouslySetInnerHTML={{ __html: c.reason ?? "" }}
              />
            </div>
            <div className="reason-line">
              <span className="reason-tag">對策：</span>
              <div
                className="html inline"
                dangerouslySetInnerHTML={{ __html: c.advice ?? "" }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* 6. 給父母的功課：本週轉變練習 */}
      <span className="item-label">給父母的功課：本週轉變練習</span>
      <div className="item-block">
        {(data.weeklyPractice ?? []).map((p, i) => (
          <div className="practice-item" key={i}>
            <span className="block-head">
              {i + 1}. {p.title}：
            </span>
            <div
              className="html"
              dangerouslySetInnerHTML={{ __html: p.content ?? "" }}
            />
          </div>
        ))}
      </div>

      {/* 7. 溫馨結語 */}
      <span className="item-label">溫馨結語</span>
      <div
        className="item-block html"
        dangerouslySetInnerHTML={{ __html: data.conclusion?.content ?? "" }}
      />

      {/* memo */}
      <div className="memo">
        <span className="product-id">{`產品編號${data.productID}`}</span>
        <div className="links">
          <a href={SHARE_URL} onClick={handleShare}>👉 點擊分享 </a>
          <span>|</span>
          <a href={LINE_URL} target="_blank" rel="noopener noreferrer">
            👉 官方LINE客服{" "}
          </a>
          <span>|</span>
          <Link to={`/consult?order=${data.orderID || ""}`}>👉 專業諮詢 </Link>
        </div>
      </div>
    </>
  );

  return (
    <div className="family-match-report-page">
      {/* 網頁版 */}
      {!isMobile && (
        <>
          <div
            className="web-main"
            onMouseEnter={() => {
              setShowServiceMenu(false);
              setShowPersonalMenu(false);
            }}
          >
            <div className="reel-main" ref={reelRef}>
              <img
                className="top-holder"
                ref={topHolderRef}
                src="/images/bg-top-reel.png"
                alt=""
              />
              <div className="outer-paper" ref={outerPaperRef}>
                <div className="inner-paper">{renderInnerContent()}</div>
              </div>
              <img
                className="bottom-holder"
                ref={bottomHolderRef}
                src="/images/bg-bottom-reel.png"
                alt=""
                style={{ opacity: 0 }}
              />
            </div>

            <SiteFooter />
          </div>
        </>
      )}

      {/* 手機版 */}
      {isMobile && (
        <div className="mobile-main">
          <div className="reel-main" ref={mobileReelRef}>
            <img
              className="top-holder"
              ref={mobileTopHolderRef}
              src="/images/bg-top-reel.png"
              alt=""
            />
            <div className="outer-paper" ref={mobileOuterPaperRef}>
              <div className="inner-paper">{renderInnerContent()}</div>
            </div>
            <img
              className="bottom-holder"
              ref={mobileBottomHolderRef}
              src="/images/bg-bottom-reel.png"
              alt=""
              style={{ opacity: 0 }}
            />
          </div>

          <SiteFooter />
        </div>
      )}
    </div>
  );
};

export default FamilyMatchReportComponent;
